import clsx from "clsx";

import { unstable__Button as Button } from "@hagerty/react-components";

import { Card } from "../../../Card/Card";
import { BillDue } from "../billDue/BillDue";
import { BillDueProps } from "../billDue/BillDue.types";
import { formatCurrency } from "../utils/formatCurrency";

import styles from "./ApexBill.module.scss";
import { Skeleton } from "./skeleton/Skeleton";
import {
  ApexApiBill,
  ApexBillProps,
  PaymentPlanPeriod,
} from "./ApexBill.types";

const getBillDueDetails = (bill: ApexApiBill) => {
  const variant: BillDueProps["variant"] = bill.isAutoPay
    ? "recursive"
    : "single";

  if (bill.billStatus === "HAS_BILL" && bill.invoiceDueAmount) {
    return (
      <>
        <Card.Title className={styles.title}>
          {formatCurrency(bill.invoiceDueAmount)}
        </Card.Title>
        <BillDue
          date={bill.invoiceDueDate}
          variant={variant}
          hasError={false}
        />
      </>
    );
  }

  if (bill.pastDueAmount) {
    return (
      <>
        <Card.Title className={styles.title}>
          {formatCurrency(bill.pastDueAmount)}
        </Card.Title>
        <BillDue variant={variant} hasError />
      </>
    );
  }

  return null;
};

export const ApexBill = ({
  bill,
  phoneNumber,
  className,
  style,
}: ApexBillProps) => {
  const isAutoPay = bill.isAutoPay;
  const productName = getProductName(bill.vehicles);
  const paymentPlan = getPaymentPlan(bill.paymentPlanPeriod, isAutoPay);
  const billStatus = bill.billStatus;
  const showCallToPay =
    !!phoneNumber && ((isAutoPay && billStatus === "FAILED") || !isAutoPay);

  if (billStatus === "NO_BILL") {
    return null;
  }

  return (
    <Card
      as="article"
      aria-label="bill"
      className={clsx(styles.card, className)}
      style={style}
    >
      <div>Enthusiast+ policy</div>
      {getBillDueDetails(bill)}
      <Card.Separator />
      <div>{productName}</div>
      {paymentPlan && (
        <>
          <Card.Separator />
          <div>{paymentPlan}</div>
        </>
      )}

      <Card.Footer as="footer" className={styles.footer}>
        {showCallToPay && (
          <div className={styles.callToPayWrapper}>
            <Button variant="solid" size="medium" href={phoneNumber.href}>
              Call to pay
            </Button>
            <p>
              In order to pay your bill, call{" "}
              <a href={phoneNumber.href}>{phoneNumber.formatted}</a>
            </p>
          </div>
        )}
      </Card.Footer>
    </Card>
  );
};

function getProductName(vehicles: string[]): string {
  const vehicleCount = vehicles.length;

  if (vehicleCount === 0) return "";

  return `${vehicles[0]}${vehicleCount > 1 ? ` +${vehicleCount - 1}` : ""}`;
}

function getPaymentPlan(period: PaymentPlanPeriod, isAutoPay: boolean): string {
  if (isAutoPay) {
    switch (period) {
      case "ANNUAL":
        return "Annual Payment Plan";
      case "SEMIANNUAL":
        return "Semi-Annual Payment Plan";
      case "QUARTERLY":
        return "Quarterly Payment Plan";
      case "MONTHLY":
        return "Monthly Payment Plan";
      default:
        return "Payment Plan";
    }
  } else {
    return "Annual premium";
  }
}

ApexBill.Skeleton = Skeleton;
